// Resume Interview Answered Summary **************************************************************
// pathname: /v1/resume/interview/answered-summary
// ************************************************************************************************
import { useSwrApi } from '@hooks/useSwrApi';
import { ApiSchema } from '@type/api/apiUtilityFn';

// Interface
// *****************************************************
export interface UseSwrResumeInterviewAnsweredProps {
  locale: string;
  shouldFetch?: boolean;
}

/**
 * @description SWR: fetch the summary of resume-interview questions the current user has answered.
 *
 * The endpoint requires authentication — pass the auth state through `shouldFetch` so the request
 * only fires for logged-in users.
 *
 * * **locale**: string. What locale to use for the request.
 * * **shouldFetch**: boolean, optional. Only fire the request when true. Default true.
 *
 * @returns `{ answeredCount, answeredQuestionIds, hasAnswered, isLoading, error, mutate }`
 */
export const useSwrResumeInterviewAnswered = (props: UseSwrResumeInterviewAnsweredProps) => {
  const { locale, shouldFetch = true } = props;

  const {
    data,
    error,
    isLoading,
    isValidating,
    mutate,
  } = useSwrApi<ApiSchema<'ResumeInterviewAnsweredSummary'>>({
    url: '/v1/resume/interview/answered-summary',
    locale,
    shouldFetch,
    apiOptions: {
      revalidateOnFocus: false,
      dedupingInterval: 1000 * 60, // 1 minute
    },
  });

  // Derived, camel-cased values
  // ********************************************
  const answeredCount = data?.answered_count ?? 0;
  const answeredQuestionIds = data?.answered_question_ids ?? [];
  const answeredQuestionTexts = data?.answered_question_texts ?? [];
  const hasAnswered = answeredCount > 0;

  return {
    answeredCount,
    answeredQuestionIds,
    answeredQuestionTexts,
    hasAnswered,
    isLoading,
    isValidating,
    error,
    mutate,
  };
};
